import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, from, switchMap, throwError } from 'rxjs';
import Session from 'supertokens-web-js/recipe/session';

const AUTH_RETRY_HEADER = 'x-nt25-auth-refresh-retry';
const SESSION_REFRESH_PATH = '/api/auth/session/refresh';

export const authSessionHttpInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.headers.has(AUTH_RETRY_HEADER) || request.url.includes(SESSION_REFRESH_PATH)) {
    return next(request);
  }

  return next(request).pipe(
    catchError((error: unknown) => {
      if (!isRecoverableSessionError(error)) {
        return throwError(() => error);
      }

      return from(Session.attemptRefreshingSession().catch(() => false)).pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            return throwError(() => error);
          }

          return next(
            request.clone({
              setHeaders: {
                [AUTH_RETRY_HEADER]: '1'
              }
            })
          );
        })
      );
    })
  );
};

export function isRecoverableSessionError(error: unknown): error is HttpErrorResponse {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  const httpError = error;

  if (isLegacyAccessTokenVerificationError(httpError)) {
    return true;
  }

  if (httpError.status !== 401) {
    return false;
  }

  const errorMessage = extractErrorMessage(httpError).toLowerCase();
  return errorMessage.includes('try refresh token') || errorMessage.includes('unauthorised') || errorMessage.includes('unauthorized');
}

export function isLegacyAccessTokenVerificationError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 500) {
    return false;
  }

  return extractErrorMessage(error).toLowerCase().includes('failed to verify access token');
}

function extractErrorMessage(error: HttpErrorResponse): string {
  if (typeof error.error === 'string') {
    return error.error;
  }

  if (typeof error.error?.message === 'string') {
    return error.error.message;
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  return '';
}
